import React, { useMemo, useState } from 'react';

import { useIntl } from 'react-intl-next';

import CloseIcon from '@atlaskit/icon/core/migration/close--cross';
import { fg } from '@atlaskit/platform-feature-flags';
import { Stack } from '@atlaskit/primitives/compiled';
import Select, { CreatableSelect } from '@atlaskit/select';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';
import { assert, AssertionError } from '@atlassian/eoc-common';
import { ErrorMessage } from '@atlassian/eoc-form';

import {
	CUSTOM_DYNAMIC_PROPERTY_SYNTAX_EXAMPLE,
	CUSTOM_DYNAMIC_PROPERTY_SYNTAX_EXAMPLE_2,
	CUSTOM_DYNAMIC_PROPERTY_SYNTAX_EXAMPLE_3,
	DEFAULT_FIELDS,
} from '../../../common/constants';
import { ConditionFilterMessages } from '../../../common/messages';
import type { IFieldOptions, OPERATION_TYPE } from '../../../common/types';
import { validateCustomDynamicPropertyValue } from '../../../common/utils';

import { Button, ConditionWrapper } from './styled';
import { type IConditionProps, TYPE_MAPPING } from './types';

const Condition: React.FC<IConditionProps> = ({
	expectedValue,
	field,
	not,
	operation,
	fields,
	update,
	delete: deleteCondition,
	conditionCount,
	isFieldKeyRequired,
	isDisabled,
	isReadOnly,
	autoFocus,
	isCustomDynamicPropertiesEnabled,
	updateCustomDynamicProperties,
}) => {
	const { formatMessage } = useIntl();
	const [invalidCustomDynamicProperty, setInvalidCustomDynamicProperty] =
		useState<IFieldOptions | null>(null);

	const fieldSelectValue = useMemo(() => {
		const matchedField = fields.find((f) => f.value === field);

		if (
			isCustomDynamicPropertiesEnabled &&
			!matchedField &&
			fg('enable-dynamic-properties-in-operations')
		) {
			return {
				label: field.replace(/{{_|}}/g, ''),
				value: field,
				type: 'string' as const,
			};
		}

		return matchedField ?? DEFAULT_FIELDS[0];
	}, [field, fields, isCustomDynamicPropertiesEnabled]);

	const Fields = TYPE_MAPPING[fieldSelectValue.type];

	const SelectComponent =
		isCustomDynamicPropertiesEnabled && fg('enable-dynamic-properties-in-operations')
			? CreatableSelect
			: Select;

	return (
		<Stack>
			<ConditionWrapper data-testid="condition-container">
				<SelectComponent
					name="alertField"
					autoFocus={autoFocus}
					options={fields}
					value={
						invalidCustomDynamicProperty &&
						isCustomDynamicPropertiesEnabled &&
						fg('enable-dynamic-properties-in-operations')
							? invalidCustomDynamicProperty
							: fieldSelectValue
					}
					isDisabled={isDisabled}
					menuIsOpen={isReadOnly ? false : undefined}
					isSearchable={!isReadOnly}
					{...(isCustomDynamicPropertiesEnabled && fg('enable-dynamic-properties-in-operations')
						? {
								isInvalid: !!invalidCustomDynamicProperty,
								onCreateOption: (value) => {
									assert(
										!!updateCustomDynamicProperties,
										new AssertionError(
											'ConditionFilter',
											"updateCustomDynamicProperties can't be empty",
										),
									);

									update({
										field: value,
										expectedValue: '',
										fieldFreeForm: false,
									});

									if (validateCustomDynamicPropertyValue(value)) {
										setInvalidCustomDynamicProperty(null);
										updateCustomDynamicProperties(value);
									} else {
										setInvalidCustomDynamicProperty({ value, label: value, type: 'string' });
									}
								},
							}
						: {})}
					onChange={(selectedKey) => {
						if (selectedKey) {
							if (
								isCustomDynamicPropertiesEnabled &&
								fg('enable-dynamic-properties-in-operations')
							) {
								setInvalidCustomDynamicProperty(null);
							}

							update({
								field: selectedKey.value,
								expectedValue: '',
								fieldFreeForm: selectedKey.type === 'keyValue',
							});
						}
					}}
					menuPosition="fixed"
					label={formatMessage(ConditionFilterMessages.AlertFieldAriaLabel)}
				/>

				<Fields
					field={field}
					fieldType={fieldSelectValue.type}
					update={update}
					expectedValue={expectedValue}
					not={not}
					operation={
						fg('enable-merged-condition-filters')
							? ((not ? `not-${operation}` : operation) as OPERATION_TYPE)
							: operation
					}
					isFieldKeyRequired={isFieldKeyRequired}
					isDisabled={isDisabled}
					isReadOnly={isReadOnly}
				/>
				{conditionCount > 1 && (
					<Button
						onClick={(e, analyticsEvent) => {
							fireUIAnalytics(analyticsEvent, 'removeFilterCondition');
							deleteCondition();
						}}
						isDisabled={isDisabled || isReadOnly}
						iconAfter={
							<CloseIcon
								label={formatMessage(ConditionFilterMessages.DeleteIconLabel)}
								color="currentColor"
							/>
						}
						appearance="subtle"
						aria-label={formatMessage(ConditionFilterMessages.RemoveConditionButtonAriaLabel)}
					/>
				)}
			</ConditionWrapper>
			{!!invalidCustomDynamicProperty && fg('enable-dynamic-properties-in-operations') && (
				<ErrorMessage>
					{formatMessage(ConditionFilterMessages.NewCustomDynamicPropertyInputError, {
						firstSyntax: CUSTOM_DYNAMIC_PROPERTY_SYNTAX_EXAMPLE,
						secondSyntax: CUSTOM_DYNAMIC_PROPERTY_SYNTAX_EXAMPLE_2,
						thirdSyntax: CUSTOM_DYNAMIC_PROPERTY_SYNTAX_EXAMPLE_3,
					})}
				</ErrorMessage>
			)}
		</Stack>
	);
};

export default Condition;
